import { descending, DSVRowArray, DSVRowString, group, groups } from "d3";
import {LocalDatasetsService} from "../../../shared/services/local-datasets.service";
import { formatNumber, formatPercent } from "@angular/common";
import { groupData } from "src/app/shared/utils/group-data";
import { DashboardColumnValueAggregator } from "../interfaces/DashboardColumnValueAggregator";
import { sortAlphabeticallyPrioritizeSigma } from "src/app/shared/utils/sort-alphabetically-prioritize-sigma";
import { kEurShort } from "src/app/shared/utils/k-eur-short";
import { toNumDE } from "src/app/shared/utils/to-num-de";
import { toPercentString } from "src/app/shared/utils/to-percent-string";
import { Store } from "@ngxs/store";
import { kEurLong } from "src/app/shared/utils/k-eur-long";
import { toOneDecimalString } from "src/app/shared/utils/to-one-decimal-string";

export interface DashboardDataTransformer {
  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null;
}


export class FilterDependantColumnDashboardDataTransformer implements DashboardDataTransformer {
  _filterName: string;
  _whenFilterEquals: string;
  _thenColumn: string;
  _elseColumn: string;
  _toNewColumn: string;

  constructor(filterName: string, whenFilterEquals: string, thenColumn: string, elseColumn: string, toNewColumn: string) {
    this._filterName = filterName;
    this._whenFilterEquals = whenFilterEquals;
    this._thenColumn = thenColumn;
    this._elseColumn = elseColumn;
    this._toNewColumn = toNewColumn;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const filter = filterValues.get(this._filterName);

    return data.map(row => {
      if (filter === this._whenFilterEquals) {
        row[this._toNewColumn] = row[this._thenColumn];
      } else {
        row[this._toNewColumn] = row[this._elseColumn];
      }
      return row;
    }) as DSVRowArray;
  }
}

export class MetricDependantColumnDashboardDataTransformer extends FilterDependantColumnDashboardDataTransformer {
  constructor(whenMetricEquals: string, thenColumn: string, elseColumn: string, toNewColumn: string) {
    super('metric', whenMetricEquals, thenColumn, elseColumn, toNewColumn);
  }
}

export class RevenueIndexedValueDependantColumnDashboardDataTransformer extends FilterDependantColumnDashboardDataTransformer {
  constructor(whenMetricEquals: string, thenColumn: string, elseColumn: string, toNewColumn: string) {
    super('revenue-index', whenMetricEquals, thenColumn, elseColumn, toNewColumn);
  }
}

export class GenderDependantColumnDashboardDataTransformer extends FilterDependantColumnDashboardDataTransformer {
  constructor(whenMetricEquals: string, thenColumn: string, elseColumn: string, toNewColumn: string) {
    super('gender', whenMetricEquals, thenColumn, elseColumn, toNewColumn);
  }
}
export class FilterValueToColumnDashboardDataTransformer implements DashboardDataTransformer {
  _filterName: string
  _filterValueColumn: { [k:string]: string }
  _resultColumn: string

  constructor(config: {filterName: string, filterValueColumn: { [k: string]: string }, resultColumn: string}) {
    this._filterName = config.filterName;
    this._filterValueColumn = config.filterValueColumn;
    this._resultColumn = config.resultColumn
  }

  transform(data: DSVRowArray<string> | null, filterValues: Map<string, any>): DSVRowArray<string> | null {
    if (!data) return null;

    const filterValue = filterValues.get(this._filterName);

    return data.map(row => ({
      ...row,
      [this._resultColumn]: row[this._filterValueColumn[filterValue]] ?? ''
    })) as DSVRowArray
  }
}

export class EconomicParticipantsTypeDependantColumnDashboardDataTransformer extends FilterValueToColumnDashboardDataTransformer {
  constructor(filterValueColumn: { [k: string]: string }, resultColumn: string) {
    super({
      filterName: 'economic_participant',
      filterValueColumn,
      resultColumn
    })
  }
}

export class FilterValueColumnMappingDashboardDataTransformer implements DashboardDataTransformer {
  _filterName: string
  _filterValueColumnRef: { [k:string]: string }
  _toNewColumn: string

  constructor(filterName: string, filterValueColumnRef: { [k: string]: string }, toNewColumn: string) {
    this._filterName = filterName;
    this._filterValueColumnRef = filterValueColumnRef;
    this._toNewColumn = toNewColumn
  }

  transform(data: DSVRowArray<string> | null, filterValues: Map<string, any>): DSVRowArray<string> | null {
    if (!data) return null;

    const filterValue = filterValues.get(this._filterName);

    return data.map(row => ({
      ...row,
      [this._toNewColumn]: row[this._filterValueColumnRef[filterValue]]
    })) as DSVRowArray
  }
}

export class EmployedTypeDependantColumnDashboardDataTransformer extends FilterValueColumnMappingDashboardDataTransformer
 {
  constructor(filterValueColumnRef: { [k: string]: string }, toNewColumn: string) {
    super('employed', filterValueColumnRef, toNewColumn)
  }
}

export class EmployedIndexDependantColumnDashboardDataTransformer extends FilterValueColumnMappingDashboardDataTransformer
 {
  constructor(filterValueColumnRef: { [k: string]: string }, toNewColumn: string) {
    super('employed-index', filterValueColumnRef, toNewColumn)
  }
}

export class FiltersValueColumnMappingDashboardDataTransformer implements DashboardDataTransformer {
  _filterNames: string[];
  _filterValuesJoinedBy: string;
  _filterValueColumnRef: { [k:string]: string };
  _toNewColumn: string;

  constructor(filterNames: string[], filterValuesJoinedBy: string, filterValueColumnRef: { [k:string]: string }, toNewColumn: string) {
    this._filterNames = filterNames
    this._filterValuesJoinedBy = filterValuesJoinedBy
    this._filterValueColumnRef = filterValueColumnRef
    this._toNewColumn = toNewColumn
  }

  transform(data: DSVRowArray<string> | null, filterValues: Map<string, any>): DSVRowArray<string> | null {
    if (!data) return null;

    const filterValueMixed = this._filterNames.map(name => filterValues.get(name)).join(this._filterValuesJoinedBy)

    return data.map(row => ({
      ...row,
      [this._toNewColumn]: row[this._filterValueColumnRef[filterValueMixed]]
    })) as DSVRowArray
  }
}

interface RankTransformerConfig {
  groupColumn?: string;
  groupRankColumn?: string
}

export class RankDashboardDataTransformer implements DashboardDataTransformer {

  private readonly _rankColumn: string;
  private readonly _valueColumn: string;
  private readonly _config?: RankTransformerConfig;

  constructor(rankColumn: string, valueColumn: string, config: RankTransformerConfig = {}) {
    this._rankColumn = rankColumn;
    this._valueColumn = valueColumn;
    this._config = config;
  }


  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    data.sort((a, b) => {
      return Number(b[this._valueColumn]!) - Number(a[this._valueColumn]!);
    });

    const globalRankedData = data.map((row, index) => {
      row[this._rankColumn] = (index + 1).toString();
      return row;
    }) as DSVRowArray;

    if (this._config?.groupColumn) {
      const grouped = groups(globalRankedData, d => String(d[this._config!.groupColumn!]))

      const groupRankedData = grouped.flatMap(([group, rows]) =>
        rows
          .slice() // shallow copy
          .sort((a, b) => descending(+a[this._valueColumn], +b[this._valueColumn]))
          .map((row, i) => ({
            ...row,
            [this._config!.groupRankColumn!]: String(i + 1)
          }))
      );

      return groupRankedData as DSVRowArray;
    } else {
      return globalRankedData
    }


  }
}

export class ConditionalRankDashboardDataTransformer implements DashboardDataTransformer {

  private readonly _rankColumn: string;
  private readonly _valueColumn: string;
  private readonly _conditionFn: (row: DSVRowString) => boolean;

  constructor({ rankColumn, valueColumn, conditionFn }: { rankColumn: string, valueColumn: string, conditionFn: (row: DSVRowString) => boolean }) {
    this._rankColumn = rankColumn;
    this._valueColumn = valueColumn;
    this._conditionFn = conditionFn;
  }


  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const dataToRank = data.filter(row => this._conditionFn(row));
    const dataNotToRank = data.filter(row => !this._conditionFn(row));

    dataToRank.sort((a, b) => {
      return Number(b[this._valueColumn]!) - Number(a[this._valueColumn]!);
    });

    const dataRanked = dataToRank.map((row, index) => {
      row[this._rankColumn] = (index + 1).toString();
      return row;
    });

    dataNotToRank.forEach(row => row[this._rankColumn] = '');

    return dataRanked.concat(dataNotToRank) as DSVRowArray;
  }
}

export class CoalesceDataTransformer implements DashboardDataTransformer {

  private readonly _columns: string[];
  private readonly _toNewColumn: string;

  constructor(columns: string[], toNewColumn: string) {
    this._columns = columns;
    this._toNewColumn = toNewColumn;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) return null;

    return data.map(row => {
      row[this._toNewColumn] = this._columns.reduce((acc, col) => acc || row[col], '');
      return row;
    }) as DSVRowArray
  }

}

export class ApplyFunctionDataTransformer implements DashboardDataTransformer {

  private readonly _function: (row: DSVRowString) => string;
  private readonly _toNewColumn: string;

  constructor(fn: (row: DSVRowString) => string, toNewColumn: string) {
    this._function = fn;
    this._toNewColumn = toNewColumn;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) return null;

    return data.map(row => {
      row[this._toNewColumn] = this._function(row);
      return row;
    }) as DSVRowArray;
  }
}

export class SortByNumericValueColumnDashboardDataTransformer implements DashboardDataTransformer {

  private readonly _valueColumn: string;
  private readonly _order: 'asc' | 'desc';

  constructor(valueColumn: string, order: 'asc' | 'desc') {
    this._valueColumn = valueColumn;
    this._order = order;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) return null;

    data.sort((a, b) => {
      return this._order === 'asc'
        ? Number(a[this._valueColumn]) - Number(b[this._valueColumn])
        : Number(b[this._valueColumn]) - Number(a[this._valueColumn]);
    });

    return data;
  }

}

export class DropColumnsDashboardDataTransformer implements DashboardDataTransformer {

  private readonly _columns: string[];

  constructor(columns: string[]) {
    this._columns = columns;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) return null;

    return data.map(row => {
      this._columns.forEach(col => delete row[col]);
      return row;
    }) as DSVRowArray;
  }

}

export class OrderByColumnsDashboardDataTransformer implements DashboardDataTransformer {

  private readonly _orderByColumns: { column: string, order: 'asc' | 'desc', type: 'numeric' | 'string', replaceEmptyWith: string }[];

  constructor(orderByColumns: { column: string, order: 'asc' | 'desc', type: 'numeric' | 'string', replaceEmptyWith: string }[]) {
    this._orderByColumns = orderByColumns;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) return null;

    data.sort((a, b) => {
      for (const { column, order, type, replaceEmptyWith } of this._orderByColumns) {
        const aValue = a[column] === '' ? replaceEmptyWith : a[column];
        const bValue = b[column] === '' ? replaceEmptyWith : b[column];

        if (type === 'numeric') {
          if (order === 'asc') {
            if (Number(aValue) < Number(bValue)) return -1;
            if (Number(aValue) > Number(bValue)) return 1;
          } else {
            if (Number(aValue) > Number(bValue)) return -1;
            if (Number(aValue) < Number(bValue)) return 1;
          }
        } else {
          if (order === 'asc') {
            if (aValue < bValue) return -1;
            if (aValue > bValue) return 1;
          } else {
            if (aValue > bValue) return -1;
            if (aValue < bValue) return 1;
          }
        }
      }

      return 0;
    });

    return data;
  }

}

export class FilterRowsByFilterValuesDashboardDataTransformer implements DashboardDataTransformer {

  private readonly _filterFn: (row: DSVRowString, filterValues: Map<string, any>) => boolean;

  constructor(filterFn: (row: DSVRowString, filterValues: Map<string, any>) => boolean) {
    this._filterFn = filterFn;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) return null;

    const filtered=  data.filter((row, _, __) => this._filterFn(row, filterValues));
    return filtered as DSVRowArray;
  }
}

export class NameAscDashboardDataTransformer implements DashboardDataTransformer {

  private readonly _valueColumn: string;

  constructor(valueColumn: string) {
    this._valueColumn = valueColumn;
  }


  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    data.sort((a,b) => {
      const nameA = a[this._valueColumn]!.toUpperCase(); // ignore upper and lowercase
      const nameB = b[this._valueColumn]!.toUpperCase(); // ignore upper and lowercase
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    });

    return data as DSVRowArray;
  }
}


export class IdNameMappingDashboardDataTransformer implements DashboardDataTransformer {

  private readonly _idColumnKey: string;
  private readonly _mappedNameColumnKey: string;
  private readonly _idNameMapping: Map<string, string>;

  constructor({ idColumnKey, mappedNameColumnKey, idNameMapping }: {
    idColumnKey: string,
    mappedNameColumnKey: string,
    idNameMapping: Map<string, string>
  }) {
    this._idColumnKey = idColumnKey;
    this._mappedNameColumnKey = mappedNameColumnKey;
    this._idNameMapping = idNameMapping;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map((row, index) => {
      const id = row[this._idColumnKey];
      if (id != null) {
        row[this._mappedNameColumnKey] = this._idNameMapping.get(id)!;
      }
      return row;
    }) as DSVRowArray;
  }
}


export class StateIdNameMappingDashboardDataTransformer extends IdNameMappingDashboardDataTransformer {
  constructor(private localDatasetsService: LocalDatasetsService) {
    const mapping = localDatasetsService.getStatesMap();
    const mappingWithStringIds = new Map<string, string>();
    mapping.forEach((value, key) => {
      if (key != null)
        mappingWithStringIds.set(key.toString(), value);
      else
        mappingWithStringIds.set('-1', value);
    });
    super({ idColumnKey: 'state_id', mappedNameColumnKey: 'state_name', idNameMapping: mappingWithStringIds });
  }
}

export class SankeyNodeNameMappingDashboardDataTransformer implements DashboardDataTransformer {
  countriesNameMap: any;
  statesNameMap: any;
  subMarketsNameMap: any;
  industriesNameMap: any;
  wzNameMap: any;

  constructor(private localDatasetsService: LocalDatasetsService) {
    this.countriesNameMap = new Map([[1, 'Deutschland']])
    this.statesNameMap = this.localDatasetsService.getStatesMap()
    this.subMarketsNameMap = this.localDatasetsService.getSubmarketMap()
    this.industriesNameMap = this.localDatasetsService.getIndustryIdNameMap()
    this.wzNameMap = this.localDatasetsService.getWzMap()
  }
  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map((row) => {
      row['source_name'] = this.getNodeName(row.source_id, row.source_type);
      row['target_name'] = this.getNodeName(row.target_id, row.target_type);
      return row;
    }) as DSVRowArray;
  }

  getNodeName(id: string, type: string) {
    if (id == null || id == '') {
      switch (type) {
        case 'country':
          return '-'
        case 'state':
          return 'Kein Bundesland'
        case 'sub_market':
          return 'Andere Teilmärkte / Anonymisiert'
        case 'industry_sub_market':
          return 'Andere Branchen'
        case 'wz':
          return 'Andere WZ-Klassen / Anonymisiert'
      }
    }
    switch (type) {
      case 'country':
        return this.countriesNameMap.get(+id)!
      case 'state':
        return this.statesNameMap.get(+id)!
      case 'sub_market':
        return this.subMarketsNameMap.get(+id)!
      case 'industry_sub_market':
        return this.industriesNameMap.get(+id)!
      case 'wz':
        // const [wzId, ...wzName] = this.wzNameMap.get(id)!.split(',')
        // return wzName.join(',').trimStart()
        // return wzId
        return this.wzNameMap.get(id)!
      default:
        return ''
    }
  }

}

export class StateIdCodeMappingDashboardDataTransformer extends IdNameMappingDashboardDataTransformer {
  constructor(private localDatasetsService: LocalDatasetsService) {
    const mapping = localDatasetsService.getStateIdCodeMap();
    const mappingWithStringIds = new Map<string, string>();
    mapping.forEach((value, key) => {
      if (key != null)
        mappingWithStringIds.set(key.toString(), value);
      else
        mappingWithStringIds.set('-1', value);
    });
    super({ idColumnKey: 'state_id', mappedNameColumnKey: 'state_code', idNameMapping: mappingWithStringIds });
  }
}

export class RegionalAssociationIdNameMappingDashboardDataTransformer extends IdNameMappingDashboardDataTransformer {
  constructor(private localDatasetsService: LocalDatasetsService) {
    const mapping = localDatasetsService.getRegionalAssociationsMap();
    const mappingWithStringIds = new Map<string, string>();
    mapping.forEach((value, key) => {
      if (key != null)
        mappingWithStringIds.set(key.toString(), value);
      else
        mappingWithStringIds.set('-1', value);
    });
    super({ idColumnKey: 'regional_association_id', mappedNameColumnKey: 'regional_association_name', idNameMapping: mappingWithStringIds });
  }
}

export class AdministrativeDistrictIdNameMappingDashboardDataTransformer extends IdNameMappingDashboardDataTransformer {
  constructor(private localDatasetsService: LocalDatasetsService) {
    const mapping = localDatasetsService.getAdministrativeDistrictsMap();
    const mappingWithStringIds = new Map<string, string>();
    mapping.forEach((value, key) => {
      if (key != null)
        mappingWithStringIds.set(key.toString(), value);
      else
        mappingWithStringIds.set('-1', value);
    });
    super({ idColumnKey: 'administrative_district_id', mappedNameColumnKey: 'administrative_district_name', idNameMapping: mappingWithStringIds });
  }
}

export class DistrictIdNameMappingDashboardDataTransformer extends IdNameMappingDashboardDataTransformer {
  constructor(private localDatasetsService: LocalDatasetsService) {
    const mapping = localDatasetsService.getDistrictsMap();
    const mappingWithStringIds = new Map<string, string>();
    mapping.forEach((value, key) => {
      if (key != null)
        mappingWithStringIds.set(key.toString(), value);
      else
        mappingWithStringIds.set('-1', value);
    });
    super({ idColumnKey: 'district_id', mappedNameColumnKey: 'district_name', idNameMapping: mappingWithStringIds });
  }
}

export class SubMarketIdNameMappingDashboardDataTransformer extends IdNameMappingDashboardDataTransformer {
  constructor(private localDatasetsService: LocalDatasetsService) {
    const mapping = localDatasetsService.getSubmarketMap();
    const mappingWithStringIds = new Map<string, string>();
    mapping.forEach((value, key) => {
      if (key != null)
        mappingWithStringIds.set(key.toString(), value);
      else
        mappingWithStringIds.set('-1', value);
    });
    super({ idColumnKey: 'sub_market_id', mappedNameColumnKey: 'sub_market_name', idNameMapping: mappingWithStringIds });
  }
}

export class IndustryIdNameMappingDashboardDataTransformer extends IdNameMappingDashboardDataTransformer {
  constructor(private localDatasetsService: LocalDatasetsService) {
    const mapping = localDatasetsService.getIndustryIdNameMap();
    const mappingWithStringIds = new Map<string, string>();
    mapping.forEach((value, key) => {
      if (key != null)
        mappingWithStringIds.set(key.toString(), value);
      else
        mappingWithStringIds.set('-1', value);
    });
    super({ idColumnKey: 'sub_market_id', mappedNameColumnKey: 'sub_market_name', idNameMapping: mappingWithStringIds });
  }
}

export class WzIdNameMappingDashboardDataTransformer extends IdNameMappingDashboardDataTransformer {
  constructor(private localDatasetsService: LocalDatasetsService, private options?: {includeId?: boolean}) {
    const mapping = localDatasetsService.getWzMap();
    const mappingWithStringIds = new Map<string, string>();
    mapping.forEach((value, key) => {
      if (key != null)
        mappingWithStringIds.set(
          key.toString(),
          options?.includeId ? value : value.split(',').slice(1).join(', ')
        );
      else
        mappingWithStringIds.set('-1', value);
    });
    super({ idColumnKey: 'wz_id', mappedNameColumnKey: 'wz_name', idNameMapping: mappingWithStringIds });
  }
}


export class UndefinedStateToSumGermanyDashboardDataTransformer implements DashboardDataTransformer {

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map((row, index) => {
      if (row['state_id'] == null || row['state_id'] === '') {
        row['state_id'] = '-1';
      }
      return row;
    }) as DSVRowArray;
  }
}
export class DefinedPeriodIdentifiersToPeriodLabelDashboardDataTransformer implements DashboardDataTransformer {
  constructor(
    public store: Store,
    public datasetName: string,
    public config: {
      enabledPeriods: ('year' | 'quarter' | 'month')[]
    } = {
      enabledPeriods: ['year', 'quarter', 'month']
    }
  ) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const enabledPeriods = this.config.enabledPeriods;

    return data.map((row) => {
      if (enabledPeriods.includes('year') && row['year'] && !row['quarter'] && !row['month']) {
        row['period'] = 'year';
        row['period_id'] = row['year'];
        row['month_year'] = ''
        row['quarter_year'] = ''
      }
      else if (enabledPeriods.includes('quarter') && row['year'] && row['quarter'] && !row['month']) {
        const quarterYear = `Q${row['quarter']}/${row['year']}`;
        row['period'] = 'quarter';
        row['period_id'] = quarterYear;
        row['quarter_year'] = quarterYear;
        row['month_year'] = ''
      }
      else if (enabledPeriods.includes('month') && row['year'] && row['quarter'] && row['month']) {
        const monthYear = `${monthNames[Number(row['month']) - 1]}/${row['year']}`;
        const quarterYear = `Q${row['quarter']}/${row['year']}`;
        row['period'] = 'month';
        row['period_id'] = monthYear
        row['month_year'] = monthYear
        row['quarter_year'] = quarterYear
      }
      else {
        row['period'] = '';
      }
      return row;
    }) as DSVRowArray;
  }
}

export class Q2ToYearPeriodIdDashboardDataTransformer implements DashboardDataTransformer {
  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    if (filterValues.get('period') === 'year') {
      return data.map((row) => {
        row['period'] = 'year'
        row['period_id'] = row['year'];
        return row;
      }) as DSVRowArray;
    } else {
      return data;
    }
  }
}

export class SourceIdToEntityIdDashboardDataTransformer implements DashboardDataTransformer {

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map((row) => {
      // if (row['target_type'] === 'state') {
      //   row['state_id'] = row['target_id'];
      // }

      // if (row['source_type'] === 'state') {
      //   row['state_id'] = row['source_id'];
      // }

      if (row['target_type'] === 'industry_sub_market') {
        row['industry_sub_market_id'] = row['target_id'];
      }

      if (row['source_type'] === 'industry_sub_market') {
        row['industry_sub_market_id'] = row['source_id'];
      }

      if (row['target_type'] === 'sub_market') {
        row['sub_market_id'] = row['target_id'];
      }

      if (row['source_type'] === 'sub_market') {
        row['sub_market_id'] = row['source_id'];
      }

      if (row['target_type'] === 'wz' ) {
        row['wz_id'] = row['target_id'];
      }

      return row;
    }) as DSVRowArray;
  }
}

export class CompanyTypeValueDisplayNameMappingDataTransformer implements DashboardDataTransformer {

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const metricValue = filterValues.get('metric')

    return data.map(row => {
      row['company_type_name'] = metricValue === 'companies' ? 'Unternehmen' : 'Kleinunternehmen'
      return row;
    }) as DSVRowArray
  }
}

export class RevenueIndexedValueDisplayNameMappingDataTransformer implements DashboardDataTransformer {

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const metricValue = filterValues.get('revenue-index')

    return data.map(row => {
      row['revenue_index_name'] = metricValue === 'revenue_per_company__k_eur' ? 'Unternehmen' : metricValue === 'revenue_per_small_company__k_eur' ? 'Kleinunternehmen' : ''
      return row;
    }) as DSVRowArray
  }
}

export class GVAIndexedValueDisplayNameMappingDataTransformer implements DashboardDataTransformer {

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const metricValue = filterValues.get('gross-value-added-index')

    return data.map(row => {
      row['gross_value_added_index_name'] = metricValue === 'gross_value_added_per_company__k_eur' ? 'Unternehmen' : ''
      return row;
    }) as DSVRowArray
  }
}

export class CompaniesCountTotalDataTransformer implements DashboardDataTransformer {

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map(row => {
      row['total_companies_count'] = (Number(row['companies_count'] || 0) + Number(row['small_companies_count'] || 0)).toString()
      return row;
    }) as DSVRowArray
  }
}

export class SumSubMarketNameDataTransformer implements DashboardDataTransformer {

  constructor(private localDatasetsService: LocalDatasetsService) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map(row => {
      const methodologyId = row['methodology_id'];

      const sumSubMarketName = methodologyId ? this.localDatasetsService.getSumSubMarketNameByMethodologyId(Number(methodologyId)) : ''

      row['sum_sub_market_name'] = sumSubMarketName

      return row;
    }) as DSVRowArray
  }
}

export class MethodologyNameDataTransformer implements DashboardDataTransformer {

  constructor(private localDatasetsService: LocalDatasetsService) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const methodologyMap = this.localDatasetsService.getMethodologyMap();

    return data.map(row => {
      const methodologyName = methodologyMap.get(+row['methodology_id']);
      row['methodology_name'] = methodologyName ?? ''
      return row;
    }) as DSVRowArray
  }
}

export class EmployedIndexValueDisplayNameMappingDataTransformer implements DashboardDataTransformer {

  transform(data: DSVRowArray<string> | null, filterValues: Map<string, any>): DSVRowArray<string> | null {
    if (!data) return null;

    const value = filterValues.get('employed-index')

    let employed_index_name: string;
    switch (value) {
      case 'employed_per_company':
        employed_index_name = 'pro Unternehmen'
        break;
      case 'employed_state_pct':
        employed_index_name = 'an Gesamtbeschäftigung (branchenübergreifend)'
        break;
      case 'employed_district_pct':
        employed_index_name = 'an Beschäftigung (branchenübergreifend)'
        break;
      case 'employed_population_pct':
        employed_index_name = 'an Bevolkerung'
        break;
      default:
        break;
    }

    return data.map(row => ({ ...row, employed_index_name })) as unknown as DSVRowArray<string>
  }
}

export class GrossValueAddedIndexNameDataTransformer implements DashboardDataTransformer {

  transform(data: DSVRowArray<string> | null, filterValues: Map<string, any>): DSVRowArray<string> | null {
    if (!data) return null;

    const value = filterValues.get('gross-value-added-index')

    let index_name: string;
    switch (value) {
      case 'gross_value_added_per_total_company':
        index_name = 'Bruttowertschöpfung pro Unternehmen insgesamt'
        break;
      case 'gross_value_added_per_employed_total__k_eur':
        index_name = 'Bruttowertschöpfung pro Gesamtbeschäftigten'
        break;
      case 'gross_value_added_per_economic_participant_total__k_eur':
        index_name = 'Bruttowertschöpfung pro Erwerbstätigen'
        break;
      case 'gross_value_added_production_value_pct':
        index_name = 'Anteil Bruttowertschöpfung am Produktionswert'
        break;
      case 'gross_value_added_per_employed_social_security__k_eur':
        index_name = 'Bruttowertschöpfung pro SV-pflichtig Beschäftigten'
        break;
      case 'gross_value_added_per_economic_participants_core_market__k_eur':
        index_name = 'Bruttowertschöpfung pro Kernerwerbstätigen'
        break;
      default:
        break;
    }

    return data.map(row => ({ ...row, index_name })) as unknown as DSVRowArray<string>
  }
}

export class EconomicParticipantsIndexNameDataTransformer implements DashboardDataTransformer {

  transform(data: DSVRowArray<string> | null, filterValues: Map<string, any>): DSVRowArray<string> | null {
    if (!data) return null;

    const value = filterValues.get('economic-participants-index')

    let economic_participants_index_name: string;
    switch (value) {
      case 'economic_participants_total_per_company':
        economic_participants_index_name = 'Erwerbstätige pro Unternehmen'
        break;
      case 'revenue_per_economic_participant_total__k_eur':
        economic_participants_index_name = 'Umsatz pro Erwerbstätigen'
        break;
      case 'economic_participants_total_population_pct':
        economic_participants_index_name = 'Anteil Erwerbstätiger an der Bevölkerung'
        break;
      case 'employed_total_economic_participants_total_pct':
        economic_participants_index_name = 'Anteil Beschäftigter an Erwerbstätigen'
        break;
      default:
        break;
    }

    return data.map(row => ({ ...row, economic_participants_index_name })) as unknown as DSVRowArray<string>
  }
}

export class IndexValueFormatDataTransformer implements DashboardDataTransformer {

  constructor(private config: { filterName: string, valueColumnKey: string, resultColumnKey: string }) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) return null;

    const indexValueFormatter = (value: string | undefined | null, filterValue: string) => {
      if (filterValue.includes('pct')) {
        return toPercentString(value);
      } else if (filterValue.includes('k_eur')) {
        return kEurLong(value);
      } else {
        return toOneDecimalString(value);
      }
    }

    return data.map(row => {
      const rowValue = row[this.config.valueColumnKey];
      const filterValue = filterValues.get(this.config.filterName);
      row[this.config.resultColumnKey] = indexValueFormatter(rowValue, filterValue)
      return row
    }) as unknown as DSVRowArray<string>
  }

}

export class EmployedTypeValueDisplayNameMappingDataTransformer implements DashboardDataTransformer {

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const metricValue = filterValues.get('employed')

    return data.map(row => {
      row['employed_type_name'] = metricValue === 'total' ? 'Gesamtbeschäftigte'
        : metricValue === 'social_security' ? 'Sv-pflichtig Beschäftigte'
          : 'Geringfügig Beschäftigte'

      return row;
    }) as DSVRowArray
  }
}

export class GrossValueAddedTypeValueDisplayNameMappingDataTransformer implements DashboardDataTransformer {

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const metricValue = filterValues.get('gross_value_added')

    return data.map(row => {
      row['gross_value_added_type_name'] = metricValue === 'production_value' ? 'Produktionswert' : 'Bruttowertschöpfung'

      return row;
    }) as DSVRowArray
  }
}

export class EconomicParticipantTypeValueDisplayNameMappingDataTransformer implements DashboardDataTransformer {

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const metricValue = filterValues.get('economic_participant')

    return data.map(row => {
      let economic_participant_type_name: string;
      switch (metricValue) {
        case 'total':
          economic_participant_type_name = 'Erwerbstätige';
          break;
        case 'core_market':
          economic_participant_type_name = 'Kernerwerbstätige';
          break;
        case 'marginal':
          economic_participant_type_name = 'Geringfügig Erwerbstätige';
          break;
        default:
          economic_participant_type_name = '';
          break;
      }
      row['economic_participant_type_name'] = economic_participant_type_name;
      return row;
    }) as DSVRowArray
  }
}
export class TouristTypeValueDisplayNameMappingDataTransformer implements DashboardDataTransformer {

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const metricValue = filterValues.get('tourist_type')

    return data.map(row => {
      let tourist_type_type_name: string;
      switch (metricValue) {
        case 'total':
          tourist_type_type_name = 'Gesamt';
          break;
        case 'foreign':
          tourist_type_type_name = 'Ausländisch';
          break;
        case 'domestic':
          tourist_type_type_name = 'Inländisch';
          break;
        default:
          tourist_type_type_name = '';
          break;
      }
      row['tourist_type_type_name'] = tourist_type_type_name;
      return row;
    }) as DSVRowArray
  }
}


export class PercentageDashboardDataTransformer implements DashboardDataTransformer {

  resultColumnKey!: string;
  valueColumnKey!: string;
  totalColumnKey!: string;
  fractionDigits!: number;


  constructor({resultColumnKey, valueColumnKey, totalColumnKey, fractionDigits = 2}: {
    resultColumnKey: string,
    valueColumnKey: string,
    totalColumnKey: string,
    fractionDigits?: number
  }) {
    this.resultColumnKey = resultColumnKey;
    this.valueColumnKey = valueColumnKey;
    this.totalColumnKey = totalColumnKey
    this.fractionDigits = fractionDigits;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map(row => {
      row[this.resultColumnKey] = ((Number(row[this.valueColumnKey]) / Number(row[this.totalColumnKey])) * 100).toFixed(this.fractionDigits).toString();
      return row;
    }) as DSVRowArray
  }
}


export class FirstYearValueToColumnDashboardDataTransformer implements DashboardDataTransformer {

  resultColumnKey!: string;
  valueColumnKey!: string;
  indexColumns!: string[];

  constructor(resultColumnKey: string, valueColumnKey: string, indexColumns: string[]) {
    this.resultColumnKey = resultColumnKey;
    this.valueColumnKey = valueColumnKey;
    this.indexColumns = indexColumns;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const firstYear = filterValues.get('years')[0];

    const lowestValuesCache = new Map();

    // find row with lowest value for each combination of index columns
    data.forEach(row => {
      const key = this.indexColumns.map(col => row[col]).join('-');
      const value = Number(row[this.valueColumnKey]);
      const lowestValue = lowestValuesCache.get(key);
      if (lowestValue === undefined || value < lowestValue) {
        lowestValuesCache.set(key, value);
      }
    });

    // set result column to value of row with lowest value for each combination of index columns
    return data.map(row => {
      const key = this.indexColumns.map(col => row[col]).join('-');
      row[this.resultColumnKey] = lowestValuesCache.get(key);
      return row;
    }) as DSVRowArray;

    // find row with first year matching all index columns
    /*const value = data.find(row => {
      return this.indexColumns.every(col => row[col] === filterValues.get(col)) && row['year'] === firstYear;
    })?.[this.valueColumnKey];

    return data.map(row => {
      row[this.resultColumnKey] = value;
      return row;
    }) as DSVRowArray;*/
  }
}

export class GroupDashboardDataTransformer implements DashboardDataTransformer {
  _groupColumnKey!: string | string[];
  _columnAggregators!: Map<string, DashboardColumnValueAggregator>;
  _keepItemsAs?: string;

  constructor(options: {
    groupColumnKey: string | string[],
    columnAggregators: Map<string, DashboardColumnValueAggregator>,
    keepItemsAs?: string
  }) {
    this._groupColumnKey = options.groupColumnKey;
    this._columnAggregators = options.columnAggregators;
    this._keepItemsAs = options.keepItemsAs;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const grouped = this._groupingStrategy(data, filterValues);

    return grouped as DSVRowArray;
  }

  _groupingStrategy(data: DSVRowArray, filterValues: Map<string, any>) {
    const result = groupData(data, filterValues, {
      groupingKey: this._groupColumnKey,
      aggregators: this._columnAggregators,
      keepItemsAs: this._keepItemsAs
    });

    return result;
  }
}

export class GroupByAdministrativeDistrictIdAndYearDashboardDataTransformer extends GroupDashboardDataTransformer {
  constructor({ valueColumnKey }: { valueColumnKey: string }) {
    super({
      groupColumnKey: ['administrative_district_id', 'year'],
      columnAggregators: new Map([
        [valueColumnKey, ({ acc, val }) => Number(acc) + Number(val)]
      ])
    });
  }
}

export class GroupByRegionalAssociationIdAndYearDashboardDataTransformer extends GroupDashboardDataTransformer {
  constructor({ valueColumnKey }: { valueColumnKey: string }) {
    super({
      groupColumnKey: ['regional_association_id', 'year'],
      columnAggregators: new Map([
        [valueColumnKey, ({ acc, val }) => Number(acc) + Number(val)]
      ])
    });
  }
}

export class MultiValueSumGroupDashboardDataTransformer extends GroupDashboardDataTransformer {
  constructor({ valueColumnKeys, groupColumnKey, keepItemAs }: { valueColumnKeys: string[], groupColumnKey: string | string[], keepItemAs?: string }) {
    super({
      groupColumnKey: groupColumnKey,
      keepItemsAs: keepItemAs,
      columnAggregators: new Map(valueColumnKeys.map((valueColumnKey) => [valueColumnKey, ({ acc, val }) => Number(acc) + Number(val)]))
    });
  }
}

export class GroupBySubMarketIdDashboardDataTransformer extends GroupDashboardDataTransformer {
  constructor({ valueColumnKey }: { valueColumnKey: string }) {
    super({
      groupColumnKey: 'sub_market_id',
      columnAggregators: new Map([
        [valueColumnKey, ({ acc, val }) => Number(acc) + Number(val)]
      ])
    });
  }
}

export class GroupBySubMarketIdAndYearDashboardDataTransformer extends GroupDashboardDataTransformer {
  constructor({ valueColumnKey }: { valueColumnKey: string }) {
    super({
      groupColumnKey: ['sub_market_id', 'year'],
      columnAggregators: new Map([
        [valueColumnKey, ({ acc, val }) => Number(acc) + Number(val)]
      ])
    });
  }
}

export class GroupByWzIdDashboardDataTransformer extends GroupDashboardDataTransformer {
  constructor({ valueColumnKey }: { valueColumnKey: string }) {
    super({
      groupColumnKey: 'wz_id',
      columnAggregators: new Map([
        [valueColumnKey, ({ acc, val }) => Number(acc) + Number(val)]
      ])
    });
  }
}

export class GroupByWzIdAndYearDashboardDataTransformer extends GroupDashboardDataTransformer {
  constructor({ valueColumnKey }: { valueColumnKey: string }) {
    super({
      groupColumnKey: ['wz_id', 'year'],
      columnAggregators: new Map([
        [valueColumnKey, ({ acc, val }) => Number(acc) + Number(val)]
      ])
    });
  }
}

export class GroupByDistrictIdDashboardDataTransformer extends GroupDashboardDataTransformer {
  constructor({ valueColumnKey }: { valueColumnKey: string }) {
    super({
      groupColumnKey: 'district_id',
      columnAggregators: new Map([
        [valueColumnKey, ({ acc, val }) => Number(acc) + Number(val)]
      ])
    });
  }
}

export class SumDistrictIndexValueDashboardDataTransformer extends GroupDashboardDataTransformer {
  constructor(options: { dividendColumnKey: string, divisorColumnKey: string, valueColumnKey: string }) {
    const { dividendColumnKey, divisorColumnKey, valueColumnKey } = options;
    super({
      groupColumnKey: 'district_id',
      columnAggregators: new Map([
        [dividendColumnKey, ({ acc, val }) => Number(acc) + Number(val)],
        [divisorColumnKey, ({ acc, val }) => Number(acc) + Number(val)],
        [valueColumnKey, ({ row }) => Math.round((Number(row[dividendColumnKey]) / Number(row[divisorColumnKey]) || 0))]
      ])
    });
  }
}

export class FilterDependantGroupDashboardDataTransformer implements DashboardDataTransformer {
  _filterName: string;
  _filterValueToGroupKeyMap: Map<string, string>;
  _aggregators!: Map<string, DashboardColumnValueAggregator>;
  _filterValueToKeepItemsLabelMap?: Map<string, string | null>;

  constructor(config: {
    filterName: string,
    filterValueToGroupKeyMap: Map<string, string>,
    aggregators: Map<string, DashboardColumnValueAggregator>,
    keepItemsLabelMap?: Map<string, string | null>
  }) {
    this._filterName = config.filterName;
    this._filterValueToGroupKeyMap = config.filterValueToGroupKeyMap;
    this._aggregators = config.aggregators;
    this._filterValueToKeepItemsLabelMap = config.keepItemsLabelMap;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const grouped = this._groupingStrategy(data, filterValues);

    return grouped as DSVRowArray;
  }

  _groupingStrategy(data: DSVRowArray, filterValues: Map<string, any>) {
    const filterValue = filterValues.get(this._filterName);

    const result = groupData(data, filterValues, {
      groupingKey: this._filterValueToGroupKeyMap.get(filterValue)!,
      aggregators: this._aggregators,
      keepItemsAs: this._filterValueToKeepItemsLabelMap?.get(filterValue)
    })

    return result;
  }
}

export class MapLevelDependantSumIndexValueDashboardDataTransformer extends FilterDependantGroupDashboardDataTransformer {
  constructor(options: { dividendColumnKey: string, divisorColumnKey: string, valueColumnKey: string }) {
    const { dividendColumnKey, divisorColumnKey, valueColumnKey } = options;
    super({
      filterName: 'map_level',
      filterValueToGroupKeyMap: new Map([
        ['administrative_districts', 'administrative_district_id'],
        ['regional_associations', 'regional_association_id'],
        ['districts', 'district_id'],
      ]),
      aggregators: new Map([
        [dividendColumnKey, ({ acc, val }) => Number(acc) + Number(val)],
        [divisorColumnKey, ({ acc, val }) => Number(acc) + Number(val)],
        [valueColumnKey, ({ row }) => Math.round((+row[dividendColumnKey] / +row[divisorColumnKey] || 0))],
        ['district_id', ({ filterValues, val }) => {
          return filterValues.get('map_level') === 'administrative_districts' || filterValues.get('map_level') === 'regional_associations' ? 'Alle' : val
        }],
        ['district_name', ({ filterValues, val }) => {
          return filterValues.get('map_level') === 'administrative_districts' || filterValues.get('map_level') === 'regional_associations' ? 'Alle' : val
        }],
        ['wz_id', ({ filterValues, val }) => filterValues.get('wz_classes').length > 1 ? 'Alle' : val],
        ['wz_name', ({ filterValues, val }) => filterValues.get('wz_classes').length > 1 ? 'Alle' : val],
      ]),
      keepItemsLabelMap: new Map([
        ['administrative_districts', 'districts'],
        ['regional_associations', 'districts'],
        ['districts', null],
      ])
    });
  }
}

export class DistrictsForAdministrativeDistrictsDashboardDataTransfomer implements DashboardDataTransformer {
  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    if (filterValues.get('map_level') === 'districts') {
      return data;
    }

    const districts = data.filter(d => d['district_id'] !== '');
    const districtsByAdministrativeId = group(districts, d => d['administrative_district_id']);

    const administrativeDistricts = data.filter(d => d['district_id'] === '');
    const administrativeDistrictWithDistricts = administrativeDistricts.map(d => ({
      ...d,
      districts: districtsByAdministrativeId.get(d['administrative_district_id'])
    }));

    return administrativeDistrictWithDistricts as unknown as DSVRowArray;
  }
}

export class DistrictsForRegionalAssociationsDashboardDataTransfomer implements DashboardDataTransformer {
  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    if (filterValues.get('map_level') === 'districts') {
      return data;
    }

    const districts = data.filter(d => d['district_id'] !== '');
    const districtsByAdministrativeId = group(districts, d => d['regional_association_id']);

    const regionalAssociations = data.filter(d => d['district_id'] === '');
    const regionalAssociationWithDistricts = regionalAssociations.map(d => ({
      ...d,
      districts: districtsByAdministrativeId.get(d['regional_association_id'])
    }));

    return regionalAssociationWithDistricts as unknown as DSVRowArray;
  }
}

export class YearlyValueChangeDataTransformer implements DashboardDataTransformer {

    idKey!: string;
    valueColumnKey!: string;
    resultColumnKey!: string;
    fractionDigits!: number;

    constructor({idKey, valueColumnKey, resultColumnKey, fractionDigits = 2}: {
      idKey: string,
      valueColumnKey: string,
      resultColumnKey: string,
      fractionDigits?: number
    }) {
      this.idKey = idKey;
      this.valueColumnKey = valueColumnKey;
      this.resultColumnKey = resultColumnKey;
      this.fractionDigits = fractionDigits;
    }

    transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
      if (!data) {
        return null;
      }

      return data.map((row) => {
        const previousRelatedRow = data.find((r) => r[this.idKey] === row[this.idKey] && +r['year'] === +row['year'] - 1);

        if (!previousRelatedRow) {
          row[this.resultColumnKey] = '';
          return row;
        }

        const previousValue = +previousRelatedRow[this.valueColumnKey];
        const currentValue = Number(row[this.valueColumnKey]);
        const valueChangePercent = previousValue !== 0 ? ((currentValue - previousValue) / previousValue) * 100 : 0;

        row[this.resultColumnKey] = valueChangePercent.toFixed(this.fractionDigits);

        return row;
      }) as DSVRowArray;
    }

}

abstract class SumAppenderDashboardDataTransformer implements DashboardDataTransformer {
  _valueKey: string;
  _idKey: string | ((row: any) => string);

  constructor(valueKey: string, idKey: string | ((row: any) => string)) {
    this._valueKey = valueKey;
    this._idKey = idKey;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray {
    if (!data || !data.length) {
      return [] as unknown as DSVRowArray;
    }

    // Pre-filter the data
    const filteredData = this.preFilterData(data, filterValues);

    const sumById = new Map<string, any>();

    for (const row of filteredData) {
      const id = typeof this._idKey === 'function' ? this._idKey(row) : row[this._idKey];

      if (sumById.has(id)) {
        const existingSum = sumById.get(id);
        existingSum[this._valueKey] = (Number(existingSum[this._valueKey]) + Number(row[this._valueKey])).toString();
      } else {
        const newRow = this.createSummedRow({
          ...row,
          [this._valueKey]: (Number(row[this._valueKey]) || 0).toString()
        });
        sumById.set(id, newRow);
      }
    }

    const sumData = Array.from(sumById.values());
    // Ensure the sorting includes the original data array
    const sortedDataWithSum = [...sumData, ...data].sort((a, b) => +b[this._valueKey] - +a[this._valueKey]);

    return sortedDataWithSum as DSVRowArray;
  }

  protected preFilterData(data: DSVRowArray, filterValues: Map<string, any>): DSVRowArray {
    // Default implementation: return the data as is
    // Override in subclasses to apply specific filtering logic
    return data;
  }

  protected abstract createSummedRow(row: DSVRowString): DSVRowString;
}

export class BaWuDistrictSumAppenderDashboardDataTransformer extends SumAppenderDashboardDataTransformer {
  marketLevelIdColumn!: string;
  marketLevelNameColumn!: string;

  constructor(valueKey: string, marketLevel: 'wz' | 'sub_market') {
    super(valueKey, 'district_id');
    this.marketLevelIdColumn = `${marketLevel}_id`;
    this.marketLevelNameColumn = `${marketLevel}_name`;
  }

  protected createSummedRow(row: DSVRowString): DSVRowString {
    return {
      ...row,
      [this.marketLevelIdColumn]: '',
      [this.marketLevelNameColumn]: 'Summe',
    };
  }

  protected override preFilterData(data: DSVRowArray, filterValues: Map<string, any>): DSVRowArray {
    return data.filter(d => d[this.marketLevelIdColumn] && d['district_id'] && d['regional_association_id']) as DSVRowArray;
  }
}

export class MarketSumAppenderDashboardDataTransformer extends SumAppenderDashboardDataTransformer {
  geoLevelIdColumn!: string;
  geoLevelNameColumn!: string;

  constructor(valueKey: string, idKey: string, geoLevel: 'district' | 'state') {
    super(valueKey, idKey);
    this.geoLevelIdColumn = `${geoLevel}_id`;
    this.geoLevelNameColumn = `${geoLevel}_name`;
  }

  protected createSummedRow(row: DSVRowString): DSVRowString {
    return {
      ...row,
      [this.geoLevelIdColumn]: '',
      [this.geoLevelNameColumn]: 'Summe',
    };
  }
}

export class GeoSumAppenderDashboardDataTransformer extends SumAppenderDashboardDataTransformer {
  marketLevelIdColumn!: string;
  marketLevelNameColumn!: string;

  constructor(valueKey: string, idKey: string, marketLevel: 'wz' | 'sub_market') {
    super(valueKey, idKey);
    this.marketLevelIdColumn = `${marketLevel}_id`;
    this.marketLevelNameColumn = `${marketLevel}_name`;
  }

  protected createSummedRow(row: DSVRowString): DSVRowString {
    return {
      ...row,
      [this.marketLevelIdColumn]: '',
      [this.marketLevelNameColumn]: 'Summe',
    };
  }
}

export class BaWuWzSumAppenderDashboardDataTransformer extends SumAppenderDashboardDataTransformer {
  constructor(valueKey: string) {
    super(valueKey, 'wz_id');
  }

  protected createSummedRow(row: any): any {
    return {
      ...row,
      regional_association_id: '',
      regional_association_name: 'Summe',
      district_id: '',
      district_name: 'Summe',
    };
  }

  protected override preFilterData(data: DSVRowArray, filterValues: Map<string, any>): DSVRowArray {
    return data.filter(d => d['wz_id'] && d['district_id'] && d['regional_association_id']) as DSVRowArray;
  }
}
export class BaWuSubMarketSumAppenderDashboardDataTransformer extends SumAppenderDashboardDataTransformer {
  constructor(valueKey: string) {
    super(valueKey, 'sub_market_id');
  }

  protected createSummedRow(row: DSVRowString) {
    return {
      ...row,
      regional_association_id: '',
      regional_association_name: 'Summe',
      district_id: '',
      district_name: 'Summe',
    };
  }
}

export class BaWuSubMarketSumFromWzSumAppenderDashboardDataTransformer extends SumAppenderDashboardDataTransformer {
  constructor(valueKey: string) {
    super(valueKey, 'sub_market_id');
  }

  protected createSummedRow(row: DSVRowString) {
    return {
      ...row,
      wz_id: '',
      wz_name: 'Summe',
      regional_association_id: '',
      regional_association_name: 'Summe',
      district_id: '',
      district_name: 'Summe',
    };
  }

  protected override preFilterData(data: DSVRowArray, filterValues: Map<string, any>): DSVRowArray {
    return data.filter(d => !d['district_id']) as DSVRowArray;
  }
}

export class BavariaDistrictSumAppenderDashboardDataTransformer extends SumAppenderDashboardDataTransformer {
  marketLevelIdColumn!: string;
  marketLevelNameColumn!: string;

  constructor(valueKey: string, marketLevel: 'wz' | 'sub_market') {
    super(valueKey, 'district_id');
    this.marketLevelIdColumn = `${marketLevel}_id`;
    this.marketLevelNameColumn = `${marketLevel}_name`;
  }

  protected createSummedRow(row: DSVRowString): DSVRowString {
    return {
      ...row,
      [this.marketLevelIdColumn]: '',
      [this.marketLevelNameColumn]: 'Summe',
    };
  }

  protected override preFilterData(data: DSVRowArray, filterValues: Map<string, any>): DSVRowArray {
    return data.filter(d => d[this.marketLevelIdColumn] && d['district_id'] && d['administrative_district_id']) as DSVRowArray;
  }
}

export class BavariaWzSumAppenderDashboardDataTransformer extends SumAppenderDashboardDataTransformer {
  constructor(valueKey: string) {
    super(valueKey, 'wz_id');
  }

  protected createSummedRow(row: any): any {
    return {
      ...row,
      administrative_district_id: '',
      administrative_district_name: 'Summe',
      district_id: '',
      district_name: 'Summe',
    };
  }

  protected override preFilterData(data: DSVRowArray, filterValues: Map<string, any>): DSVRowArray {
    return data.filter(d => d['wz_id'] && d['district_id'] && d['administrative_district_id']) as DSVRowArray;
  }
}
export class BavariaSubMarketSumAppenderDashboardDataTransformer extends SumAppenderDashboardDataTransformer {
  constructor(valueKey: string) {
    super(valueKey, 'sub_market_id');
  }

  protected createSummedRow(row: DSVRowString) {
    return {
      ...row,
      administrative_district_id: '',
      administrative_district_name: 'Summe',
      district_id: '',
      district_name: 'Summe',
    };
  }
}

export class BavariaSubMarketSumFromWzSumAppenderDashboardDataTransformer extends SumAppenderDashboardDataTransformer {
  constructor(valueKey: string) {
    super(valueKey, 'sub_market_id');
  }

  protected createSummedRow(row: DSVRowString) {
    return {
      ...row,
      wz_id: '',
      wz_name: 'Summe',
      administrative_district_id: '',
      administrative_district_name: 'Summe',
      district_id: '',
      district_name: 'Summe',
    };
  }

  protected override preFilterData(data: DSVRowArray, filterValues: Map<string, any>): DSVRowArray {
    return data.filter(d => !d['district_id']) as DSVRowArray;
  }
}

export class RegionalAssociationWzSumAppenderDashboardDataTransformer extends SumAppenderDashboardDataTransformer {
  constructor(valueKey: string) {
    super(valueKey, (row) => `${row['regional_association_id']}_${row['wz_id']}`);
  }

  protected createSummedRow(row: DSVRowString) {
    return {
      ...row,
      district_id: '',
      district_name: 'Summe',
    };
  }
}

export class AdministrativeDistrictWzSumAppenderDashboardDataTransformer extends SumAppenderDashboardDataTransformer {
  constructor(valueKey: string) {
    super(valueKey, (row) => `${row['administrative_district_id']}_${row['wz_id']}`);
  }

  protected createSummedRow(row: DSVRowString) {
    return {
      ...row,
      district_id: '',
      district_name: 'Summe',
    };
  }
}

export class AggregatedRowChildrenAttacherDashboardDataTransfomer implements DashboardDataTransformer {
  aggregateIdColumn!: string;
  childrenIdColumn!: string;
  resultColumn!: string;

  constructor(args: { aggregateIdColumn: string, childrenIdColumn: string, resultColumn: string }) {
    this.aggregateIdColumn = args.aggregateIdColumn
    this.childrenIdColumn = args.childrenIdColumn
    this.resultColumn = args.resultColumn
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const children = data.filter(d => d[this.childrenIdColumn] !== '' && d[this.aggregateIdColumn] !== '');
    const childrenByAggregateId = group(children, d => d[this.aggregateIdColumn]);

    // attach children to the aggregate entry
    for (const d of data) {
      if (d[this.aggregateIdColumn] !== '' && d[this.childrenIdColumn] === '' && !d.hasOwnProperty(this.resultColumn)) {
        const childrenCopy = JSON.parse(JSON.stringify(childrenByAggregateId.get(d[this.aggregateIdColumn])!));
        (d as any)[this.resultColumn] = childrenCopy;
      }
    }

    return data;
  }
}

abstract class EmployedDataSumAppenderDashboardDataTransformer implements DashboardDataTransformer {
  protected _idKey: string;
  protected _totalValueKey: string;
  protected _marginalvalueKey: string;
  protected _socialSecurityValueKey: string;

  constructor(idKey: string, options: {
    totalValueKey: string
    marginalvalueKey: string
    socialSecurityValueKey: string
  }) {
    this._idKey = idKey;
    this._totalValueKey = options.totalValueKey;
    this._marginalvalueKey = options.marginalvalueKey;
    this._socialSecurityValueKey = options.socialSecurityValueKey;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray {
    if (!data || !data.length) {
      return [] as unknown as DSVRowArray;
    }

    const sumById = new Map<string, any>();

    const filteredData = this.filterData(data);

    for (const row of filteredData) {
      const id = row[this._idKey];

      if (sumById.has(id)) {
        const existingSum = sumById.get(id);
        existingSum[this._totalValueKey] = (Number(existingSum[this._totalValueKey]) + Number(row[this._totalValueKey])).toString();
        existingSum[this._marginalvalueKey] = (Number(existingSum[this._marginalvalueKey]) + Number(row[this._marginalvalueKey])).toString();
        existingSum[this._socialSecurityValueKey] = (Number(existingSum[this._socialSecurityValueKey]) + Number(row[this._socialSecurityValueKey])).toString();
      } else {
        const newRow = {
          ...row,
          ...this.getSummedIdAndName(),
          [this._totalValueKey]: (Number(row[this._totalValueKey]) || 0).toString(),
          [this._marginalvalueKey]: (Number(row[this._marginalvalueKey]) || 0).toString(),
          [this._socialSecurityValueKey]: (Number(row[this._socialSecurityValueKey]) || 0).toString(),
        };
        sumById.set(id, newRow);
      }
    }

    const sumData = Array.from(sumById.values());
    return [...sumData, ...data] as DSVRowArray;
  }

  protected abstract filterData(data: DSVRowArray): DSVRowArray;

  protected abstract getSummedIdAndName(): DSVRowString;
}

export class EmployedStatesSumAppenderDashboardDataTransformer extends EmployedDataSumAppenderDashboardDataTransformer {
  constructor(options: {
    totalValueKey: string
    marginalvalueKey: string
    socialSecurityValueKey: string
  }) {
    super('state_id', options);
  }

  protected filterData(data: DSVRowArray): DSVRowArray {
    return data;
  }

  protected getSummedIdAndName() {
    return {
      district_id: '',
      district_name: 'Summe',
    };
  }
}

export class EmployedSubMarketsSumAppenderDashboardDataTransformer extends EmployedDataSumAppenderDashboardDataTransformer {
  constructor(options: {
    totalValueKey: string
    marginalvalueKey: string
    socialSecurityValueKey: string
  }) {
    super('sub_market_id', options);
  }

  protected filterData(data: DSVRowArray): DSVRowArray {
    return data.filter(d => d['district_id'] === '') as DSVRowArray;
  }

  protected getSummedIdAndName() {
    return {
      state_id: '',
      state_name: 'Summe',
      district_id: '',
      district_name: 'Summe',
    }
  }
}


export class AddExportSidePropertyDashboardDataTransformer implements DashboardDataTransformer {

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map((row, index) => {
      row['export_side'] = filterValues.get('export_side');
      return row;
    }) as DSVRowArray;
  }
}

export class FilterPassedStatesAndDistrictsBySideDashboardDataTransformer implements DashboardDataTransformer {

  private readonly _statesFilterValueKeyPrefix: string;
  private readonly _districtsFilterValueKeyPrefix: string;

  constructor({ statesFilterValueKeyPrefix, districtsFilterValueKeyPrefix }: {
    statesFilterValueKeyPrefix: string,
    districtsFilterValueKeyPrefix: string,
  }) {
    this._statesFilterValueKeyPrefix = statesFilterValueKeyPrefix;
    this._districtsFilterValueKeyPrefix = districtsFilterValueKeyPrefix;
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    const exportSideValue = filterValues.get('export_side').toLowerCase();
    const statesFilterValueKey = `${this._statesFilterValueKeyPrefix}_${exportSideValue}`;
    const districtsFilterValueKey = `${this._districtsFilterValueKeyPrefix}_${exportSideValue}`;

    return data.filter(item => item[statesFilterValueKey] === 'true' && item[districtsFilterValueKey] === 'true') as DSVRowArray;
  }
}


export class CAGRDashboardDataTransformer implements DashboardDataTransformer {

  valueColumnKey!: string;
  yearColumnKey!: string;
  resultColumnKey!: string;
  fractionDigits!: number;
  indexColumns!: string[];
  quarterColumnKey?: string; // optional

  constructor({
    valueColumnKey,
    yearColumnKey = 'year',
    resultColumnKey,
    indexColumns,
    fractionDigits = 2,
    quarterColumnKey = 'quarter',
  }: {
    valueColumnKey: string,
    yearColumnKey?: string,
    resultColumnKey: string,
    indexColumns: string[],
    fractionDigits?: number,
    quarterColumnKey?: string,
  }) {
    this.valueColumnKey = valueColumnKey;
    this.yearColumnKey = yearColumnKey;
    this.indexColumns = indexColumns;
    this.resultColumnKey = resultColumnKey;
    this.fractionDigits = fractionDigits;
    this.quarterColumnKey = quarterColumnKey;
  }

  private normalizeQuarter(row: any): string | null {
    // Returns "1" | "2" | "3" | "4" | "null" | null (when quarter not used at all)
    if (!this.quarterColumnKey) return null;
    const raw = row[this.quarterColumnKey];

    // If the dataset doesn't actually have this column, treat as no-quarter mode
    if (typeof raw === 'undefined' && !(this.quarterColumnKey in row)) return null;

    if (raw === null || raw === undefined || raw === '') return 'null';
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 1 && n <= 4) return String(n as 1 | 2 | 3 | 4);
    // Fallback: coerce anything else into a string bucket
    return String(raw);
  }

  private keyFor(row: any, quarterBucket: string | null): string {
    // Include quarter bucket in the key only when quarter is being used
    const parts = this.indexColumns.map(col => row[col]);
    if (quarterBucket !== null) {
      parts.push(`q:${quarterBucket}`);
    }
    return parts.join('—'); // use an uncommon separator to avoid collisions
  }

  transform(data: DSVRowArray | null, _filterValues: Map<string, any>): DSVRowArray | null {

    if (!data) return null;

    // Build baselines per (index key × quarter group if applicable)
    const baselines: Map<string, { startYear: number, startValue: number }> = new Map();

    for (const row of data) {
      const value = Number(row[this.valueColumnKey]);
      const year = Number(row[this.yearColumnKey]);
      const quarterBucket = this.normalizeQuarter(row);
      const key = this.keyFor(row, quarterBucket);

      if (!Number.isFinite(value) || !Number.isFinite(year)) continue;

      // Skip rows with a value of 0 to prevent division by zero in the CAGR calculation
      if (value === 0) continue;

      const baseline = baselines.get(key);
      if (!baseline || year < baseline.startYear) {
        baselines.set(key, { startYear: year, startValue: value });
      }
    }

    return data.map(row => {
      const quarterBucket = this.normalizeQuarter(row);
      const key = this.keyFor(row, quarterBucket);
      const baseline = baselines.get(key);

      // No baseline -> set empty
      if (!baseline) {
        row[this.resultColumnKey] = '';
        return row;
      }

      const { startYear, startValue } = baseline;

      const currentYear = Number(row[this.yearColumnKey]);
      const currentValue = Number(row[this.valueColumnKey]);

      if (!Number.isFinite(currentYear) || !Number.isFinite(currentValue)) {
        row[this.resultColumnKey] = '';
        return row;
      }

      if (currentYear < startYear) {
        row[this.resultColumnKey] = '';
        return row;
      }

      // Handle baseline year gracefully (0-year period -> 0% by convention here)
      if (currentYear === startYear) {
        row[this.resultColumnKey] = '';
        return row;
      }

      // Standard CAGR over the number of years since the (same-quarter) baseline
      const years = currentYear - startYear;
      // Guard against division by zero or invalid baseline
      if (!Number.isFinite(startValue) || startValue === 0 || years <= 0) {
        row[this.resultColumnKey] = '';
        return row;
      }

      const cagr = (Math.pow(currentValue / startValue, 1 / years) - 1) * 100;
      row[this.resultColumnKey] = Number.isFinite(cagr) ? cagr.toFixed(this.fractionDigits) : '';

      return row;
    }) as DSVRowArray;
  }
}



export class PercentageValueDataTransformer implements DashboardDataTransformer {

  constructor(private valueColumnKey: string, private resultColumnKey?: string) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map(row => {
      const value = row[this.valueColumnKey];

      row[this.resultColumnKey ?? this.valueColumnKey] = (value == 'NaN' || value == '') ? '' : `${formatPercent(parseFloat(value) / 100, 'de_DE', '1.0-2')}`;

      return row;
    }) as DSVRowArray
  }
}

export class KEuroToEuroValueDataTransformer implements DashboardDataTransformer {

    constructor(private valueColumnKey: string, private config: { resultColumnKey?: string, addEur: boolean } = { resultColumnKey: undefined, addEur: true }) {}

    transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
      if (!data) {
        return null;
      }

      return data.map(row => {
        const value = row[this.valueColumnKey];

        let result = (value == undefined || value == 'NaN' || value.trim() == '') ? '' : `${formatNumber(parseFloat(value) * 1000, 'de_DE')}`;

        if (this.config.addEur && value != '') {
          result += ' EUR';
        }

        row[this.config.resultColumnKey ?? this.valueColumnKey.replace('__k_eur', '')] = result;

        return row;
      }) as DSVRowArray
    }
}

export class SortAlphabeticallyByColumnDataTransformer implements DashboardDataTransformer {

  constructor(private columnKey: string) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.sort((a, b) => {
      const valueA = a[this.columnKey];
      const valueB = b[this.columnKey];

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return 1;   // A goes after B
      if (valueB == null) return -1;  // B goes after A

      return sortAlphabeticallyPrioritizeSigma(
        valueA.toUpperCase(),
        valueB.toUpperCase()
      );
    }) as DSVRowArray;
  }
}


export class SortBySubMarketDataTransformer implements DashboardDataTransformer {

  columnKey: string = 'sub_market_name';
  constructor() {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.sort((a, b) => {
      const nameA = a[this.columnKey]!.toUpperCase(); // ignore upper and lowercase
      const nameB = b[this.columnKey]!.toUpperCase(); // ignore upper and lowercase
      return nameA.localeCompare(nameB);
    }) as DSVRowArray;
  }
}

export class SortByWzIdDataTransformer implements DashboardDataTransformer {

  columnKey: string = 'wz_id';
  constructor() {}

  _removeWz08FromWZId(wzId: string): string {
    return wzId.replace('WZ08-', '');
  }

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.sort((a, b) => {
      return Number(this._removeWz08FromWZId(a[this.columnKey])) - Number(this._removeWz08FromWZId(b[this.columnKey]));
    }) as DSVRowArray;
  }
}

export class SortByDistrictIdAndYearDataTransformer implements DashboardDataTransformer {
  constructor(
    private districtIdColumn: string = 'district_id',
    private yearColumn: string = 'year'
  ) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.sort((a, b) => {
      // Sort by district_id (as string), then by year (as number)
      if (+a[this.districtIdColumn] < +b[this.districtIdColumn]) return -1;
      if (+a[this.districtIdColumn] > +b[this.districtIdColumn]) return 1;
      return Number(a[this.yearColumn]) - Number(b[this.yearColumn]);
    }) as DSVRowArray;
  }
}
export class SortByAdministrativeDistrictIdAndYearDataTransformer implements DashboardDataTransformer {
  constructor(
    private administrativeDistrictIdColumn: string = 'administrative_district_id',
    private yearColumn: string = 'year'
  ) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.sort((a, b) => {
      // Sort by district_id (as string), then by year (as number)
      if (+a[this.administrativeDistrictIdColumn] < +b[this.administrativeDistrictIdColumn]) return -1;
      if (+a[this.administrativeDistrictIdColumn] > +b[this.administrativeDistrictIdColumn]) return 1;
      return Number(a[this.yearColumn]) - Number(b[this.yearColumn]);
    }) as DSVRowArray;
  }
}

export class SortByRegionalAssociationIdAndYearDataTransformer implements DashboardDataTransformer {
  constructor(
    private regionalAssociationIdColumn: string = 'regional_association_id',
    private yearColumn: string = 'year'
  ) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.sort((a, b) => {
      // Sort by district_id (as string), then by year (as number)
      if (+a[this.regionalAssociationIdColumn] < +b[this.regionalAssociationIdColumn]) return -1;
      if (+a[this.regionalAssociationIdColumn] > +b[this.regionalAssociationIdColumn]) return 1;
      return Number(a[this.yearColumn]) - Number(b[this.yearColumn]);
    }) as DSVRowArray;
  }
}

export class SortBySubMarketIdAndYearDataTransformer implements DashboardDataTransformer {

  subMarketColumn: string = 'sub_market_id';
  yearColumn: string = 'year'

  constructor() {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.sort((a, b) => {
      if (+a[this.subMarketColumn] < +b[this.subMarketColumn]) return -1;
      if (+a[this.subMarketColumn] > +b[this.subMarketColumn]) return 1;
      return Number(a[this.yearColumn]) - Number(b[this.yearColumn]);
    }) as DSVRowArray;
  }
}

export class SortByWzIdAndYearDataTransformer implements DashboardDataTransformer {

  wzIdColumn: string = 'wz_id';
  yearColumn: string = 'year'

  constructor() {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.sort((a, b) => {
      if (+a[this.wzIdColumn] < +b[this.wzIdColumn]) return -1;
      if (+a[this.wzIdColumn] > +b[this.wzIdColumn]) return 1;
      return Number(a[this.yearColumn]) - Number(b[this.yearColumn]);
    }) as DSVRowArray;
  }
}

export class SortByYearDataTransformer implements DashboardDataTransformer {
  constructor() {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.sort((a, b) => Number(a['year']) - Number(b['year'])) as DSVRowArray;
  }
}

export class SortByTimeColumnDataTransformer implements DashboardDataTransformer {

  constructor(private mode: 'year' | 'quarter' | 'month') {}

  transform(data: DSVRowArray | null): DSVRowArray | null {
    if (!data) return null

    const comparator = this.getComparator()

    return data.sort(comparator) as DSVRowArray
  }

  private getComparator() {
    switch (this.mode) {

      case "month":
        return (a: any, b: any) => {
          const dy = Number(a.year) - Number(b.year)
          if (dy !== 0) return dy
          const dq = Number(a.quarter) - Number(b.quarter)
          if (dq !== 0) return dq
          return Number(a.month) - Number(b.month)
        }

      case "quarter":
        return (a: any, b: any) => {
          const dy = Number(a.year) - Number(b.year)
          if (dy !== 0) return dy
          return Number(a.quarter) - Number(b.quarter)
        }

      case "year":
      default:
        return (a: any, b: any) => Number(a.year) - Number(b.year)

    }
  }
}

export class CalculatedColumnDataTransformer implements DashboardDataTransformer {

  constructor(private resultColumnKey: string, private calculationFn: (row: DSVRowString) => string) {}
  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map(row => {
      row[this.resultColumnKey] = this.calculationFn(row);
      return row;
    }) as DSVRowArray;
  }
}

export class ForecastYearLabelDataTransformer implements DashboardDataTransformer {
  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map(row => {
      row['year'] = row['forecast_method_id'] ? '**' + row['year'] : row['year']
      return row
    }) as DSVRowArray;
  }
}

export class KEurAbbreviationDashboardDataTransformer implements DashboardDataTransformer {
  constructor(private config: { valueColumnKey: string, resultColumnKey: string }) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map(row => {
      row[this.config.resultColumnKey] = kEurShort(row[this.config.valueColumnKey]);
      return row;
    }) as DSVRowArray
  }

}

export class NumDEDashboardDataTransformer implements DashboardDataTransformer {
  constructor(private config: { valueColumnKey: string, resultColumnKey: string, unit?: string }) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map(row => {
      row[this.config.resultColumnKey] = toNumDE(row[this.config.valueColumnKey]) + ' ' + (this.config.unit ?? '');
      return row;
    }) as DSVRowArray
  }
}

export class ValueColumnDashboardDataTransformer implements DashboardDataTransformer {
  constructor(private config: { valueColumnKey: string, resultColumnKey: string }) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map(row => {
      row[this.config.resultColumnKey] = row[this.config.valueColumnKey];
      return row;
    }) as DSVRowArray
  }
}

export class ChartFieldDashboardDataTransformer implements DashboardDataTransformer {
  constructor(private fieldName: string, private fieldFn: (row: DSVRowString<string>) => string) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map(row => {
      row[this.fieldName] = this.fieldFn(row);
      return row;
    }) as DSVRowArray
  }
}

export class RankingBarValuePctDisplayDashboardDataTransformer implements DashboardDataTransformer {
  constructor(private config: { granularityId: number, valueColumnKey: string, resultColumnKey: string }) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map(row => {
      let pctScope = ''

      switch (this.config.granularityId) {
        case 3:
          pctScope = 'Dt.'
          break;
        case 4:
          pctScope = 'Bundesland'
          break;
        default:
          pctScope = row['sum_sub_market_name']
          break;
      }

      const pctValueDisplay = row[this.config.valueColumnKey] ? toPercentString(row[this.config.valueColumnKey]) : '--';
      row[this.config.resultColumnKey] = `Anteil an ${pctScope}: ${pctValueDisplay}`;
      return row;
    }) as DSVRowArray
  }
}

export class RankingValuePctScopeDashboardDataTransformer implements DashboardDataTransformer {
  constructor(private config: { granularityId: number, resultColumnKey: string }) {}

  transform(data: DSVRowArray | null, filterValues: Map<string, any>): DSVRowArray | null {
    if (!data) {
      return null;
    }

    return data.map(row => {
      let pctScope = ''

      switch (this.config.granularityId) {
        case 3:
          pctScope = 'Dt.'
          break;
        case 4:
          pctScope = 'Bundesland'
          break;
        default:
          pctScope = row['sum_sub_market_name']
          break;
      }

      row[this.config.resultColumnKey] = pctScope;
      return row;
    }) as DSVRowArray
  }
}